/**
 * Flappy Bird Game
 * Fly through pipes to score points
 */

import React, { useState, useEffect, useRef } from 'react';
import { useLocalStorage } from './hooks/useLocalStorage';
import GameLayout from './common/GameLayout';
import ScoreDisplay from './common/ScoreDisplay';
import { GAME_CONFIG } from './config/gameConfig';

const FlappyBird = ({ onHome }) => {
  const canvasRef = useRef(null);
  const [gameState, setGameState] = useState(GAME_CONFIG.states.MENU);
  const [score, setScore] = useLocalStorage('flappybird_score', 0);
  const [highScore, setHighScore] = useLocalStorage('flappybird_highScore', 0);

  const gameStateRef = useRef({
    bird: { x: 50, y: 150, velocity: 0, radius: 10 },
    pipes: [],
    pipeCounter: 0,
  });

  const CANVAS_WIDTH = 400;
  const CANVAS_HEIGHT = 600;
  const GRAVITY = 0.6;
  const FLAP_STRENGTH = -12;
  const PIPE_WIDTH = 50;
  const PIPE_GAP = 120;

  const startGame = () => {
    gameStateRef.current = {
      bird: { x: 50, y: 150, velocity: 0, radius: 10 },
      pipes: [],
      pipeCounter: 0,
    };
    setScore(0);
    setGameState(GAME_CONFIG.states.PLAYING);
  };

  const resetGame = () => {
    if (score > highScore) setHighScore(score);
    setGameState(GAME_CONFIG.states.MENU);
  };

  useEffect(() => {
    if (gameState !== GAME_CONFIG.states.PLAYING) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const game = gameStateRef.current;

    const gameLoop = setInterval(() => {
      // Update bird
      game.bird.velocity += GRAVITY;
      game.bird.y += game.bird.velocity;

      // Check ground collision
      if (game.bird.y + game.bird.radius > CANVAS_HEIGHT) {
        setGameState(GAME_CONFIG.states.GAME_OVER);
        if (score > highScore) setHighScore(score);
      }

      // Update pipes
      game.pipes = game.pipes.filter((pipe) => pipe.x > -PIPE_WIDTH);
      game.pipes.forEach((pipe) => {
        pipe.x -= 5;

        // Collision detection
        if (
          game.bird.x + game.bird.radius > pipe.x &&
          game.bird.x - game.bird.radius < pipe.x + PIPE_WIDTH
        ) {
          if (
            game.bird.y - game.bird.radius < pipe.top ||
            game.bird.y + game.bird.radius > pipe.top + PIPE_GAP
          ) {
            setGameState(GAME_CONFIG.states.GAME_OVER);
            if (score > highScore) setHighScore(score);
          }
        }

        // Score point
        if (pipe.x === 0) {
          setScore((prev) => prev + 100);
        }
      });

      // Add pipes
      game.pipeCounter++;
      if (game.pipeCounter > 90) {
        const gapStart = Math.random() * (CANVAS_HEIGHT - PIPE_GAP - 100) + 50;
        game.pipes.push({ x: CANVAS_WIDTH, top: gapStart });
        game.pipeCounter = 0;
      }

      // Draw
      ctx.fillStyle = '#87CEEB';
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

      // Draw bird
      ctx.fillStyle = GAME_CONFIG.colors.accent2;
      ctx.beginPath();
      ctx.arc(game.bird.x, game.bird.y, game.bird.radius, 0, Math.PI * 2);
      ctx.fill();

      // Draw pipes
      ctx.fillStyle = GAME_CONFIG.colors.primary;
      game.pipes.forEach((pipe) => {
        // Top pipe
        ctx.fillRect(pipe.x, 0, PIPE_WIDTH, pipe.top);
        // Bottom pipe
        ctx.fillRect(pipe.x, pipe.top + PIPE_GAP, PIPE_WIDTH, CANVAS_HEIGHT - (pipe.top + PIPE_GAP));
      });
    }, 30);

    const handleKeyDown = (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        game.bird.velocity = FLAP_STRENGTH;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      clearInterval(gameLoop);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [gameState, score, highScore]);

  return (
    <GameLayout
      title="Flappy Bird 🐦"
      score={score}
      highScore={highScore}
      gameState={gameState}
      onRestart={resetGame}
      onHome={onHome}
    >
      <div style={styles.gameArea}>
        {gameState === GAME_CONFIG.states.MENU && (
          <div style={styles.menuContainer}>
            <h2 style={styles.menuTitle}>Flappy Bird</h2>
            <p style={styles.menuDescription}>
              Fly through the pipes! Press Space to flap.
            </p>
            <button onClick={startGame} className="btn btn-primary btn-lg" style={{ marginTop: '1rem' }}>
              🎮 Start Game
            </button>
          </div>
        )}

        {gameState === GAME_CONFIG.states.PLAYING && (
          <>
            <canvas ref={canvasRef} width={CANVAS_WIDTH} height={CANVAS_HEIGHT} style={styles.canvas} />
            <div style={styles.controls}>Press SPACE to Flap</div>
          </>
        )}

        {gameState === GAME_CONFIG.states.GAME_OVER && (
          <div style={styles.gameOverCard}>
            <h1 style={styles.gameOverTitle}>Game Over</h1>
            <ScoreDisplay score={score} stats={{ 'Final Score': score }} />
            <button onClick={startGame} className="btn btn-primary mt-3" style={{ width: '100%' }}>
              🔄 Play Again
            </button>
          </div>
        )}
      </div>
    </GameLayout>
  );
};

const styles = {
  gameArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1.5rem',
    width: '100%',
  },
  menuContainer: {
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: GAME_CONFIG.radius.lg,
    padding: '3rem 2rem',
    textAlign: 'center',
  },
  menuTitle: {
    color: GAME_CONFIG.colors.accent,
    fontSize: '2rem',
    marginBottom: '1rem',
    fontWeight: 'bold',
  },
  menuDescription: {
    color: GAME_CONFIG.colors.light,
  },
  canvas: {
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: '4px',
  },
  controls: {
    color: GAME_CONFIG.colors.light,
    fontSize: '0.9rem',
    backgroundColor: `rgba(0, 212, 255, 0.1)`,
    border: `1px solid ${GAME_CONFIG.colors.accent}`,
    borderRadius: GAME_CONFIG.radius.md,
    padding: '1rem',
  },
  gameOverCard: {
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.accent}`,
    borderRadius: GAME_CONFIG.radius.lg,
    padding: '2rem',
    textAlign: 'center',
    maxWidth: '400px',
  },
  gameOverTitle: {
    color: GAME_CONFIG.colors.danger,
    marginBottom: '1rem',
    fontSize: '2rem',
  },
};

export default FlappyBird;
